// Scroll animations. Without JS, everything is shown in its final state.

import {animateNumber, stopNumberAnimation} from '../lib/animate.ts'

/** Elements that fade in when scrolled into view. Differs from lightray-website's animations.js, where this is copied from. */
const FADING_ELEMENTS_SELECTOR = '.addFadeAnimation,.card:not(.guide-card, .calc-card, .calc-overview-card),.video-container'

/** Fades elements in once scrolled 15% into the bottom of the viewport, and out again when scrolled back down below it */
export function addFadeAnimations() {
    const fadeObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
            entry.target.classList.remove('invisible') // no longer needed from here on
            entry.target.classList.toggle('visible', entry.isIntersecting)
        })
    }, {
        threshold: 0,
        rootMargin: '100% 0% -15% 0%' // keep over-scrolled content visible (100%) but only show when scrolled 15% into the bottom viewport
    })
    document.querySelectorAll(FADING_ELEMENTS_SELECTOR).forEach(element => {
        element.classList.add('invisible', 'fade-animation')
        fadeObserver.observe(element)
    })
}

/**
 * Plays an intro on every `.countUpOnView` element whenever it scrolls into view: each `.countUp` number inside counts up from 0, and CSS animates
 * whatever depends on `.is-pending` (e.g. the stars of `.store-rating`). The HTML holds the final numbers, so it's correct without JS.
 */
export function addCountUpAnimations() {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return

    const DURATION = 3000 // ms, matches the star fill transition in reviews.scss
    const countUp = (number: HTMLElement) => {
        const target = Number(number.dataset.count?.replace(/\D/g, ''))
        animateNumber(number, 0, target, DURATION, value => Math.round(value).toLocaleString('en-US'))
    }
    const rewind = (element: Element) => {
        element.classList.add('is-pending')
        element.querySelectorAll<HTMLElement>('.countUp').forEach(number => {
            stopNumberAnimation(number)
            number.textContent = '0'
        })
    }

    // starts once scrolled 15% into the bottom of the viewport, like the fade animations
    const playObserver = new IntersectionObserver(entries => entries.forEach(({target, isIntersecting}) => {
        if (!isIntersecting || !target.classList.contains('is-pending')) return
        target.classList.remove('is-pending')
        target.querySelectorAll<HTMLElement>('.countUp').forEach(countUp)
    }), {rootMargin: '0% 0% -15% 0%'})
    // rewinds only once out of sight, so scrolling back and forth across the start line doesn't restart it
    const rewindObserver = new IntersectionObserver(entries => entries.filter(entry => !entry.isIntersecting).forEach(({target}) => rewind(target)))

    document.querySelectorAll('.countUpOnView').forEach(element => {
        element.querySelectorAll<HTMLElement>('.countUp').forEach(number => number.dataset.count = number.textContent ?? '')
        rewind(element)
        playObserver.observe(element)
        rewindObserver.observe(element)
    })
}
